import React, {
  useEffect,
  useState,
  useRef,
  RefObject,
  useCallback,
} from 'react';
import { motion } from 'framer-motion';
import classNames from 'classnames';
import { useFieldArray } from 'react-hook-form';
import { DataExtractionTemplateState } from '../../DataExtractionTemplate';
import styles from './Wizard.module.scss';
import { Button, Heading, Icon, IconList, Text } from 'components/core';
import { Inline, Stack } from 'components/layout';

export const wizardStyles = styles;

/**
 * WizardMetaData is a type that defines the structure of metadata used by the Wizard component.
 * Each key in the object represents a step in the wizard, identified by a number.
 *
 * Properties:
 * - `left`: Contains information about the left column of the wizard step.
 *   - `heading` (string): The title or heading for the left column.
 *   - `optional` (boolean, optional): Indicates if the step is optional.
 *   - `description` (JSX.Element | string): A description or additional information for the step.
 *   - `fields` (JSX.Element): The form fields or interactive elements for the step.
 *
 * - `right`: Contains information about the right column of the wizard step.
 *   - `description` (JSX.Element | string): A description or context for the right column.
 *   - `image` (string): The URL of an image to display in the right column.
 */
export type WizardMetaData = {
  [key: number]: {
    left: {
      heading: string;
      optional?: boolean;
      description: JSX.Element | string;
      fields: JSX.Element;
    };
    right: {
      description: JSX.Element | string;
      image: string;
    };
  };
};

export type WizardPendoData = {
  skip: string;
  back: string;
  next: string;
  done: string;
  setupNow: string;
  editSetup: string;
};

interface WizardProps {
  id: string;
  data: WizardMetaData;
  pendoKey: WizardPendoData;
  isOpen: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  onStepChange?: (currentStep: number) => void;
  onStepsChange?: (totalSteps: number) => void;
}

interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
}

interface ColumnProps {
  children: React.ReactNode;
  offWhiteVariant?: boolean;
  innerRef?: RefObject<HTMLDivElement>;
}

const ProgressBar = React.memo(
  ({ currentStep, totalSteps }: ProgressBarProps) => {
    const progressWidth = (currentStep / totalSteps) * 100 + '%';
    function setInitialWidth() {
      if (currentStep === 1) {
        return (currentStep / totalSteps) * 100 + '%';
      }

      return ((currentStep - 1) / totalSteps) * 100 + '%';
    }

    return (
      <div className={styles.ProgressBarFrame}>
        <motion.div
          className={styles.ProgressBarFilling}
          animate={{ width: progressWidth }}
          initial={{ width: setInitialWidth() }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          key={currentStep}
        />
      </div>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';

const Column = React.memo(
  ({ children, offWhiteVariant, innerRef }: ColumnProps) => {
    const columnClasses = classNames({
      [styles.Column]: true,
      [styles.OffWhite]: offWhiteVariant,
    });

    return (
      <div ref={innerRef} className={columnClasses}>
        {children}
      </div>
    );
  }
);

Column.displayName = 'Column';

interface NavigationProps {
  currentStep: number;
  totalSteps: number;
  handleClose: () => void;
  pendoKey: WizardPendoData;
  leftColHasScrollBar: boolean;
  firstStep: boolean;
  finalStep: boolean;
  setCurrentStep: (step: number) => void;
}

const Navigation = React.memo(
  ({
    currentStep,
    totalSteps,
    handleClose,
    pendoKey,
    leftColHasScrollBar,
    firstStep,
    finalStep,
    setCurrentStep,
  }: NavigationProps) => {
    return (
      <div
        className={classNames(styles.NavContainer, {
          [styles.NavBoxShadow]: leftColHasScrollBar,
        })}
      >
        <div className={styles.NavContent}>
          <span>
            <Text
              size="xs"
              variant="neutral"
            >{`Step ${currentStep} of ${totalSteps}`}</Text>
            <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
          </span>
          <Button
            type="weak"
            variant="ghost"
            className={styles.SkipButton}
            onClick={handleClose}
            data-pendo-key={pendoKey.skip}
          >
            Skip
          </Button>
        </div>
        <Inline spacing={4}>
          {!firstStep && (
            <Button
              className={styles.ButtonWidth}
              variant="ghost"
              onClick={() => setCurrentStep(currentStep - 1)}
              data-pendo-key={pendoKey.back}
            >
              <Icon icon={IconList.light.faArrowLeft} />
              Back
            </Button>
          )}
          {!finalStep && (
            <Button
              className={styles.ButtonWidth}
              type="brand"
              onClick={() => setCurrentStep(currentStep + 1)}
              data-pendo-key={pendoKey.next}
            >
              Next
              <Icon icon={IconList.light.faArrowRight} />
            </Button>
          )}
          {finalStep && (
            <Button
              className={styles.ButtonWidth}
              type="brand"
              onClick={handleClose}
              data-pendo-key={pendoKey.done}
            >
              Done
            </Button>
          )}
        </Inline>
      </div>
    );
  }
);

Navigation.displayName = 'Navigation';

const Wizard = ({
  id,
  data,
  pendoKey,
  isOpen,
  onClose,
  onOpen,
  onStepChange,
  onStepsChange,
}: WizardProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = Object.keys(data).length;
  const firstStep = currentStep === 1;
  const finalStep = currentStep === totalSteps;
  const leftColumnRef: RefObject<HTMLDivElement> = useRef(null);
  const [leftColHasScrollBar, setLeftColHasScrollBar] = useState(false);

  // Prop callbacks
  useEffect(() => {
    if (onOpen) {
      onOpen();
    }
    if (onStepsChange) {
      onStepsChange(totalSteps);
    }
  }, [onOpen, onStepsChange, totalSteps]);

  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep);
    }
  }, [currentStep, onStepChange]);

  // Close modal on escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  });

  // Prevent parent scrolling when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      // Reset to first step when closing
      setCurrentStep(1);
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Check if left column is scrollable, for conditional box shadow style on nav
  const interventions = useFieldArray<DataExtractionTemplateState>({
    name: 'interventions.interventions',
  });
  const characteristics = useFieldArray<DataExtractionTemplateState>({
    name: 'interventions.characteristics',
  });
  useEffect(() => {
    function updateScrollState() {
      const leftCol = leftColumnRef.current as HTMLDivElement;
      leftCol &&
        setLeftColHasScrollBar(
          leftCol.scrollHeight > leftCol.getBoundingClientRect().height
        );
    }

    updateScrollState();

    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [currentStep, interventions, characteristics]);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  const imageSrc = data[currentStep].right.image;

  return (
    <div role="dialog" aria-label={id} className={styles.WizardContainer}>
      <Column innerRef={leftColumnRef}>
        <div className={styles.WizardSection}>
          <section className={styles.WizardContent}>
            <Stack spacing={2}>
              <Heading type="h3">
                {data[currentStep].left.heading}
                {data[currentStep].left.optional ? (
                  <Text variant="weak" component="span">
                    &nbsp;(Optional)
                  </Text>
                ) : (
                  <></>
                )}
              </Heading>
              {data[currentStep].left.description}
              {data[currentStep].left.fields}
            </Stack>
          </section>

          <Navigation
            currentStep={currentStep}
            totalSteps={totalSteps}
            handleClose={handleClose}
            pendoKey={pendoKey}
            leftColHasScrollBar={leftColHasScrollBar}
            firstStep={firstStep}
            finalStep={finalStep}
            setCurrentStep={setCurrentStep}
          />
        </div>
      </Column>

      <Column offWhiteVariant>
        <Stack className={styles.WizardContent} spacing={5}>
          <Heading type="h4">
            How this relates to extracting data from a study
          </Heading>
          {data[currentStep].right.description}
          <img
            className={wizardStyles.Image}
            src={imageSrc}
            alt="context aid"
          />
        </Stack>
      </Column>
    </div>
  );
};

export default Wizard;
